<template>
	<div class="bg-white grow flex flex-col overflow-y-auto scrollbar-hide">
		<FormKit
			:id="formId"
			v-slot="{ node }"
			ref="form"
			v-model="vModel"
			type="form"
			form-class="flex flex-col grow relative"
			:disabled="disableForm"
			:actions="false"
			:data="data"
			v-on="events.form"
			@submit-invalid="currentLoadingButton = null"
		>
			<div class="sticky top-0 w-full p-5 z-20 flex bg-primary">
				<slot name="header">
					<div
						class="flex flex-row flex-wrap justify-between items-start gap-5 w-full"
					>
						<div>
							<slot
								name="header-title"
								v-bind="{ resourceTitle: resource?.title }"
							>
								<h1 class="text-lg font-medium">
									{{ customTitle ?? resource?.title }}
									{{ type === "Create" ? "aanmaken" : "bewerken" }}
								</h1>
							</slot>
							<AppAlert type="error"
							          v-if="Object.keys(node.context.messages).length > 0">
								De volgende fouten zijn opgetreden:
								<div class="mt-1 max-h-24 2xl:max-h-36 w-full overflow-y-auto">
									<FormKitMessages :sections-schema="{
                    messages: {
                      $el: 'ul',
                      attrs: {
                        class: 'list-disc list-inside'
                      }
                    },
                message: {
                  $el: 'li',
                  attrs: {
                    class: ''
                  },
                }
              }"/>
								</div>
							</AppAlert>
							<slot name="header-message"/>
						</div>
						<div class="flex items-center gap-1 text-black">
							<slot name="before-actions"
							      v-bind="{ node, submit: () => submit(vModel), hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							/>
							<slot
								name="header-actions"
								v-bind="{ node, submit: () => submit(vModel), hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							>
								<div role="group">
									<AppButton
										v-if="!hideSave"
										id="form-save"
										v-tooltip="'Opslaan (Ctrl+S)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'form-save' ? 'fa-spinner' : 'fa-floppy-disk'"
										:spin="currentLoadingButton == 'form-save'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click="saveAndNew = false;saveAndCreateRow = false;closeOnSubmitSuccess=false;currentLoadingButton='form-save'"
									>
										Opslaan
									</AppButton>
									<AppButton
										v-if="!hideSaveAndNew"
										id="form-save-and-new-btn"
										v-tooltip="'Opslaan en nieuw (Ctrl+Shift+S)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'form-save-and-new-btn' ? 'fa-spinner' : 'fa-floppy-disk-circle-arrow-right'"
										:spin="currentLoadingButton == 'form-save-and-new-btn'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click="saveAndNew = true;currentLoadingButton='form-save-and-new-btn'"
									>
										en nog een aanmaken
									</AppButton>
									<AppButton
										v-if="showRowCreateSave"
										id="form-save-and-new-row-btn"
										v-tooltip="`Opslaan en nieuwe ${resource.title.toLowerCase() } regel aanmaken`"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'form-save-and-new-row-btn' ? 'fa-spinner' : 'fa-floppy-disk-pen'"
										:spin="currentLoadingButton == 'form-save-and-new-row-btn'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click="saveAndCreateRow = true;currentLoadingButton='form-save-and-new-row-btn'"
									>
										en naar regel aanmaken
									</AppButton>
									<AppButton
										v-if="!hideSaveAndClose"
										id="form-save-and-close-btn"
										v-tooltip="'Opslaan en sluiten (Ctrl+S+F4)'"
										type="submit"
										:icon="hasActiveSubmitRequest && currentLoadingButton == 'form-save-and-close-btn' ? 'fa-spinner' : 'fa-floppy-disk-circle-xmark'"
										:spin="currentLoadingButton == 'form-save-and-close-btn'"
										:disabled="!isReady || disableSubmit || hasActiveSubmitRequest"
										@click="closeOnSubmitSuccess = true;currentLoadingButton='form-save-and-close-btn'"
									>
										en sluiten
									</AppButton>
								</div>
							</slot>
							<slot name="after-actions"
							      v-bind="{ node, submit: () => submit(vModel), hasActiveSubmitRequest, setLoadingButton, currentLoadingButton }"
							/>
						</div>
					</div>
				</slot>
			</div>
			<div
				v-if="formArrayDefinition && Object.values(formObjectDefinition).length > 0"
				class="flex flex-row flex-wrap h-full"
			>
				<slot
					v-bind="{
            formNode,
            formArrayDefinition,
            schema: formObjectDefinition as any as RequiredNonNullableObject<T>
          }"
				>
					<div class="flex flex-col flex-wrap gap-5 p-5">
						<template
							v-for="inputSchema in formArrayDefinition"
							:key="inputSchema.name"
						>
							<slot
								:name="inputSchema.name"
								v-bind="{ inputSchema }"
							>
								<FormKitSchema
									:schema="inputSchema"
									outer-class="col-span-full"
								/>
							</slot>
						</template>
					</div>
				</slot>
			</div>
			<AppDataGridLookupPromise ref="gridLookup"/>
		</FormKit>
	</div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { FormKitNode, setErrors, submitForm as formkitSubmitForm } from "@formkit/core";
import { FormKitMessages } from "@formkit/vue";
import { AxiosError, AxiosResponse } from "axios";
import { reduce } from "lodash";
import { ModelRef, ref } from "vue";
import AppDataGridLookupPromise from "~/components/AppDataGridLookupPromise.vue";
import { translate } from "~/plugins/I18n.ts";
import { useApiResources } from "~/stores/ApiResources";
import { useAppTabs } from "~/stores/useAppTabs";
import { FormKitAutocompleteLookupEvent, RequiredNonNullableObject } from "~/types";
import { getFormName, useForm } from "~/utils/Forms/useForm";
import { isFormSubmissionError } from "~/utils/zod/formSubmissionError";
import { onFormMounted, onInputMounted } from "~utils/Forms/FormUIUtils.ts";
import { refreshGridsOfResource } from "~utils/Grids/RefreshGrid.ts";
import { processConfirmation } from "~utils/ConfirmationHelper.ts";
import diff from "microdiff";

const props = withDefaults(
	defineProps<{
		// Used to fetch the form definition
		resourceKey: string;
		type: "Edit" | "Create";
		resourceId?: string;
		resourceUrl?: string;
		modelValue?: T;
		formName?: string;
		formId?: string;
		// Custom submit url
		submitUrl?: string;
		redirectUrl?: string;
		submitText?: string;
		submitIcon?: string;
		submitTooltip?: string;
		customTitle?: string;
		closeOnSubmitSuccess?: boolean;
		disableSubmit?: boolean;
		hideSave?: boolean;
		hideSaveAndNew?: boolean;
		hideSaveAndClose?: boolean;
		showRowCreateSave?: boolean;
		disableForm?: boolean;
		onBeforeSubmit?: (data: T, node: FormKitNode) => Promise<T> | T;
	}>(),
	{
		disableForm: false,
		hideSave: false,
		hideSaveAndNew: false,
		hideSaveAndClose: false,
		showRowCreateSave: false,
		closeOnSubmitSuccess: false,
		formId: Math.random().toString(36),
		onBeforeSubmit: async (data: T) => data,
	},
);
const closeOnSubmitSuccess = ref(props.closeOnSubmitSuccess);
const { disableSubmit, formId } = toRefs(props);
const hasActiveSubmitRequest = ref(false);
const currentLoadingButton = ref<null | string>(null);

const defaultSaveMenuItems = computed(() => {
	const items = [
		{
			disabled: disableSubmit.value,
			text: props.submitText ?? "Opslaan",
			icon: props.submitIcon ?? "fa-save",
			id: "save",
			action: () => {
				saveAndNew.value = false;
				formkitSubmitForm(formId.value);
			},
		},
	];

	if (props.hideSaveAndNew == false) {
		items.push({
			disabled: disableSubmit.value,
			text: "Opslaan en nieuw",
			icon: "fa-floppy-disk-circle-arrow-right",
			id: "save-and-new",
			action: () => {
				saveAndNew.value = true;
				formkitSubmitForm(formId.value);
			},
		});
	}

	return items;
});

const { error, success } = useToast();
const { setCurrentTabTitle, closeCurrentTab } = useAppTabs();
const { push, previousUrl, replace } = useRouter();
const { findByUri, findByName } = useApiResources();
const saveAndNew = ref(false);
const saveAndCreateRow = ref(false);
const dirty = ref(false);
const data = reactive({});
const vModel = defineModel<T>({} as T) as ModelRef<T>;
const resource = findByName(props.resourceKey);

const keys = useMagicKeys({
	onEventFired: (e) => {
		if (e.key === "s" && e.ctrlKey) {
			e.preventDefault();

			if (e.shiftKey) {
				saveAndNew.value = true;
				setLoadingButton("form-save-and-new");
			} else {
				setLoadingButton("form-save");
			}
		}
	},
	passive: false,
});

const watchKeys = whenever(keys.ctrl_s, () => {
	formNode.submit();
});

onActivated(() => {
	watchKeys.resume();
});

onDeactivated(() => {
	watchKeys.pause();
});

const formName = props.formName ?? getFormName(resource.name, props.type);
const emit = defineEmits<(e: "submit-success", data: T) => void>();

const { isReady, formArrayDefinition, formObjectDefinition, submitForm, onSubmitError, onSubmitSuccess } = useForm<T>(
	formName,
	resource,
	props.resourceId,
	vModel,
);

onSubmitError((e) => {
	currentLoadingButton.value = null;
	// TODO: Remove ZOD
	const submissionError = isFormSubmissionError(e);
	if (submissionError.success) {
		const { data } = submissionError;
		if (e.status == "PRECONDITION_REQUIRED") {
			processConfirmation(e.parameters.errors, vModel.value, submit);
			return;
		}
		console.log(data);

		data.violations?.forEach((violation) => {
			formNode.emit("field-constraint-violation", violation);
		});

		setErrors(
			props.formId,
			data.violations != null ? data.violations?.map((it) => it.message) : data.message,
			reduce(
				data.violations || [],
				(obj, v) => {
					if (!(v.field in obj)) {
						obj[v.field] = [];
					}

					obj[v.field].push(v.message);
					return obj;
				},
				{},
			),
		);
	} else {
		const responseData = (e as AxiosError).response.data;
		if (responseData != undefined && "localizedMessage" in responseData) {
			error(responseData.localizedMessage);
		} else {
			console.log(e);
			error("Er is iets onbekend misgegaan bij het opslaan van het formulier.");
		}
	}
});

onSubmitSuccess(async (res: AxiosResponse) => {
	let id = null;
	if (res.status == 201 && "id" in res.data) {
		id = res.data.id;
	}

	if (saveAndNew.value) {
		await closeCurrentTab(false);
		let url = props.redirectUrl ?? `/${resource.name}`;
		url += "/create";
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}

		await replace(url);
	} else if (closeOnSubmitSuccess.value) {
		await closeCurrentTab();
	} else if (saveAndCreateRow.value) {
		await closeCurrentTab();
		const resourceName = resource.name.replace(/order/, "order-row");
		const url = `/${resourceName}/create?resourceUrl=/api/${resource.name}/${id ?? props.resourceId}/rows`;
		await replace(props.redirectUrl ?? url);
	} else if (props.type == "Create" && id != null) {
		await closeCurrentTab();
		let url = props.redirectUrl ?? `/${resource.name}`;
		url += `/update/${id}`;
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}
		await replace(url);
	} else if (props.type == "Edit") {
		// Edit page, close tab then open new create tab
		await closeCurrentTab();
		let url = props.redirectUrl ?? `/${resource.name}`;
		url += `/update/${props.resourceId}`;
		if (props.resourceUrl) {
			url += `?resourceUrl=${props.resourceUrl}`;
		}
		await replace(url);
	}

	emit("submit-success", vModel.value);
	success(translate(`entities.${resource.name}`) + " opgeslagen.");

	refreshGridsOfResource(resource.name, id);
});

let formNode: FormKitNode;
const formNodeRef = ref<FormKitNode>(formNode);
const gridLookup = ref<InstanceType<typeof AppDataGridLookupPromise>>();

function setLoadingButton(buttonName?: string) {
	currentLoadingButton.value = buttonName;
}

async function tryLookup(lookupEvent: FormKitAutocompleteLookupEvent) {
	const resource = findByUri(lookupEvent.payload.originalOptionSource.replace("/reference", ""));
	try {
		if (!resource) {
			throw new Error(`Resource not found for uri: ${lookupEvent.payload.originalOptionSource}`);
		}

		const result = await gridLookup.value?.start({
			resource,
			gridKey: lookupEvent.payload.gridKey,
			selectionMode: lookupEvent.payload.selectionMode,
			customResourceUrl: lookupEvent.payload.optionsSourceWithDependencies,
			filters: lookupEvent.payload.filters,
		});

		if (result) {
			if (result.length === 1 && lookupEvent.payload.selectionMode === "singleRow") {
				lookupEvent.origin.input(result[0]);
			} else if (result.length >= 1 && lookupEvent.payload.selectionMode === "multiRow") {
				lookupEvent.origin.input(result);
			}
		}
	} catch (e) {
		console.error(e);
	}
}

watch(
	vModel,
	(newVal) => {
		const oldVal = formNode.value;
		const modelDiff = diff(oldVal, newVal);

		modelDiff.forEach((diff) => {
			if (["REMOVE"].includes(diff.type)) {
				return;
			}

			if (diff.path[0] == "contacts") {
				console.log(diff);
			}

			if (newVal[diff.path[0]] == null || formObjectDefinition.value[diff.path[0]] == null) {
				return;
			}

			if (diff.path.length == 2 && formObjectDefinition.value[diff.path[0]].value != null) {
				formObjectDefinition.value[diff.path[0]].value[diff.path[1]] = newVal[diff.path[0]][diff.path[1]];
				return;
			}

			formObjectDefinition.value[diff.path[0]].value = newVal[diff.path[0]];
		});
	},
	{ deep: true },
);

onCloseTab((e) => {
	if (dirty.value && !confirm("Er zijn onopgeslagen wijzigingen. Wilt u doorgaan?")) {
		e.prevent = true;
	}
});

async function submit(formValues: T) {
	formNode.emit("pre-submit", formValues);

	if (formNode.context && Object.values(formNode.context.messages).filter((msg) => msg.blocking).length > 0) {
		currentLoadingButton.value = null;
		return;
	}

	hasActiveSubmitRequest.value = true;
	const data = await props.onBeforeSubmit(formValues, null!);
	const submitMethod = props.type === "Create" ? "post" : "put";
	await submitForm({
		data,
		method: submitMethod,
		submitUrl: props.submitUrl || resource.uri,
	}).finally(() => {
		hasActiveSubmitRequest.value = false;
		currentLoadingButton.value = null;
		refreshParentGrid();
	});
}

function refreshParentGrid() {
	if (props.resourceKey.includes("row")) {
		const parentResource = props.resourceKey.split("-row")[0];
		refreshGridsOfResource(parentResource);
	}
}

const events = {
	form: {
		node: (e: FormKitNode) => {
			formNode = e;
			formNodeRef.value = e;

			formNode.on("update:property", (event) => {
				vModel.value[event.payload.name] = event.payload.value;
			});

			// Remove tabindex from dropdown buttons.
			e.on("child", onInputMounted);

			watch(
				isReady,
				async () => {
					onFormMounted();
					console.log("isReady");
					await new Promise((r) =>
						setTimeout(() => {
							watchOnce(vModel, () => {
								console.log("vModel change");
								dirty.value = true;
							});
						}, 200),
					);
				},
				{ immediate: isReady.value, once: true },
			);

			return e.on("autocompleteLookup.deep", tryLookup);
		},
		submit,
	},
};

provide("formNode", formNodeRef);

defineExpose({ isReady });
</script>

<style scoped></style>
